import { AppDispatch, RootState } from "@/app/store";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import ThemeSwitch from "../component/ThemeSwitch";
import { loginUser, setError } from "../features/auth/authSlice";
import { setCookieToken } from "../utils";
import { Button } from "@nextui-org/button";

const Login: React.FC = () => {
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [viewPass, setViewPass] = useState<boolean>(false);
  const theme = useSelector((state: RootState) => state.theme.theme);

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>(); // Use AppDispatch type for dispatch

  const { loading, error, isAuthenticated } = useSelector((state: RootState) => state.auth);

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(setError("")); // Reset any previous errors

    if (loading) {
      return; // Skip if already loading
    }
    const email: string = username.toLowerCase();
    try {
      // Dispatch loginUser async action and wait for the result
      await dispatch(loginUser({ username: email, password })).unwrap(); // Use unwrap to get the result or error
    } catch (error) {
      dispatch(setError("Login failed. Please check your credentials.")); // Set error in Redux if login fails
    }
  };

  useEffect(() => {
    // Redirect to /chat if authenticated
    if (isAuthenticated) {
      navigate("/chat");
    }
  }, [isAuthenticated]);

  const handlePass = () => {
    setViewPass(!viewPass);
  };

  useEffect(() => {
    document.documentElement.className = theme; // Dynamically update the theme
    setCookieToken("theme", theme); // Save the theme in cookies
  }, [theme]);

  return (
    <div className="transition-all duration-300 ease-soft-spring h-screen w-screen bg-[#F0F0F0] overflow-hidden dark:bg-gradient-to-r dark:from-[#07131C] dark:to-[#0C2538] md:pt-10 pt-0">
      {/* Theme Toggle */}
      <div className="auth_container">
        <div className="flex justify-end lg:-mr-10">
          <ThemeSwitch />
        </div>
        <div className="max-w-[45.5rem] w-full mt-10">
          <img alt="Your Company" src={logo} className="w-auto h-20" />
          <h2 className="mt-10 auth_head dark:text-white">
            Hello! <br /> <span className="text-[#265376] font-proximaBold">Log in to continue</span>
          </h2>
          <div className="mt-10 max-w-[27.25rem]">
            <form onSubmit={handleSubmit} className="space-y-3" autoComplete="off">
              {/* Email Input */}
              <div className="relative dark:bg-[#031523] bg-[#e4e4e4] rounded-xl backdrop-blur-[20px]">
                <input
                  aria-autocomplete="none"
                  id="email"
                  name="email"
                  type="email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="email"
                  className="block w-full px-5 py-4 text-sm text-black bg-transparent rounded-lg appearance-none auth_input font-ProximaLight focus:outline-none dark:text-white peer"
                  placeholder=" "
                />
                <label
                  htmlFor="email"
                  className="absolute font-ProximaLight text-base text-[#5E829F] dark:text-[#5E829F] duration-300 transform -translate-y-4 scale-75 top-3 z-10 origin-[0] px-5 peer-focus:px-2  peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-3 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/3 rtl:peer-focus:left-auto start-1"
                >
                  Email Address
                </label>
              </div>
              <div className="relative dark:bg-[#031523] bg-[#e4e4e4] rounded-xl backdrop-blur-[20px]">
                <input
                  aria-autocomplete="none"
                  id="password"
                  name="password"
                  type={viewPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder=" "
                  autoComplete="new-password"
                  className="block w-full px-5 py-4 text-sm text-black bg-transparent rounded-lg appearance-none auth_input font-ProximaLight focus:outline-none dark:text-white peer"
                />
                <label
                  htmlFor="password"
                  className="absolute font-ProximaLight text-base text-[#5E829F] dark:text-[#5E829F] duration-300 transform -translate-y-4 scale-75 top-3 z-10 origin-[0] px-5 peer-focus:px-2  peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-3 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
                >
                  Password
                </label>
                <button
                  className="absolute text-black transform -translate-y-1/2 dark:text-white top-1/2 right-3"
                  onClick={handlePass}
                  type="button"
                >
                  {viewPass ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M12 9a3 3 0 0 0-3 3a3 3 0 0 0 3 3a3 3 0 0 0 3-3a3 3 0 0 0-3-3m0 8a5 5 0 0 1-5-5a5 5 0 0 1 5-5a5 5 0 0 1 5 5a5 5 0 0 1-5 5m0-12.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 256 256"
                    >
                      <path
                        fill="currentColor"
                        d="M228 175a8 8 0 0 1-10.92-3l-19-33.2A123.2 123.2 0 0 1 162 155.46l5.87 35.22a8 8 0 0 1-6.58 9.21a8.4 8.4 0 0 1-1.29.11a8 8 0 0 1-7.88-6.69l-5.77-34.58a133 133 0 0 1-36.68 0l-5.77 34.58A8 8 0 0 1 96 200a8.4 8.4 0 0 1-1.32-.11a8 8 0 0 1-6.58-9.21l5.9-35.22a123.2 123.2 0 0 1-36.06-16.69L39 172a8 8 0 1 1-13.94-8l20-35a153.5 153.5 0 0 1-19.3-20a8 8 0 1 1 12.46-10c16.6 20.54 45.64 45 89.78 45s73.18-24.49 89.78-45a8 8 0 1 1 12.44 10a153.5 153.5 0 0 1-19.3 20l20 35a8 8 0 0 1-2.92 11"
                      />
                    </svg>
                  )}
                </button>
              </div>
              <Link
                to={"/forgot-password"}
                className="block ml-auto text-xs text-right font-ProximaLight dark:text-white dark:hover:text-gray-300 w-fit"
              >
                Forgot password?
              </Link>
              {/* Submit Button */}
              <div>
                <Button
                  type="submit"
                  className="mt-5 h-auto font-ProximaLight flex w-full justify-center rounded-2xl bg-[#265376] px-3 py-4 text-white hover:bg-[#1F374A] dark:hover:bg-white dark:hover:text-black text-base"
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Log In"}
                </Button>
              </div>
              {error && <p className="text-red-500">{error}</p>} {/* Display error message */}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
